import { createBattle, forfeitBattle, stepBattle, type BattleContext } from '../battle/battle';
import { applyDuelLevelStats } from '../battle/levelStats';
import { fightsAs, type BattleState } from '../battle/types';
import type { GameState } from '../game/types';
import type { BattleStart, TickBundle } from './protocol';

/**
 * How many ticks ahead of its own simulation a client stamps its inputs. The server uses an
 * input on the tick it names; one that arrives late is used on the next tick instead, so the
 * value only tunes how often inputs land exactly where the player meant them.
 */
export const INPUT_DELAY_TICKS = 2;

/**
 * One lockstep step, shared by the server and every client so the same bundle always produces
 * the same state: a forfeit settles the duel before the step (which then does nothing).
 */
export function applyTickBundle(
  ctx: BattleContext,
  state: BattleState,
  bundle: TickBundle,
): BattleState {
  if (bundle.forfeit) forfeitBattle(state, bundle.forfeit);
  return stepBattle(ctx, state, bundle.inputs);
}

/**
 * The duel a `battle` message starts, built as the server built it: the arena AI's sides scaled
 * to the levels the start names. The server and every client build theirs here, so the copies
 * they step through the same bundles start out identical.
 */
export function createServedBattle(
  ctx: BattleContext,
  game: GameState,
  start: Pick<BattleStart, 'seed' | 'levels'>,
): BattleState {
  const state = createBattle(ctx, game, start.seed);
  for (const side of state.seats) {
    const level = start.levels?.[side];
    const fighter = state.fighters[side];
    if (level && fighter) applyDuelLevelStats(fighter, ctx.game.roster[fightsAs(fighter)]!, level);
  }
  return state;
}
